import { CloseOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import commaNumber from 'comma-number';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { Divider, Flex, Space, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import Sorter from '@/components/DataTable/Sorter';
import List from '@/components/List/List';
import Typo from '@/components/Typography/Typo';
import AskSynclyPanel from '@/features/AskSyncly/components/AskSynclyPanel';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useKeymap from '@/hooks/pure/useKeymap';
import { cn } from '@/lib/cn';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
  type DetailModalOptions,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { detailModalStyle } from '@/styles/style';

import { FEEDBACK_SORT_OPTIONS } from '../../configs/feedback-sort-options';
import { useFeedbackSortState } from '../../hooks/useFeedbackSortState';
import FeedbackShortItem from '../ContentItem/FeedbackShortItem';
import type { FeedbackListModalOptions } from './types';

type Props = {
  alias: string;
  pos: number;
  options: DetailModalOptions<FeedbackListModalOptions>;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const FeedbackListModal = (props: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const setPopup = usePopup();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const chatMap = useHeySynclyStore((state) => state.chatMap);
  const leaveChat = useHeySynclyStore((state) => state.leaveChat);
  const [chatAlias, setChatAlias] = useState<string | null>(null);
  const [sortStatus, setSortStatus] = useFeedbackSortState();
  const chat = chatAlias ? chatMap.get(chatAlias) : undefined;
  const [selectedFeedback, setSelectedFeedback] = useState<string | null>(null);

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        props.alias,
        ...props.options.queryKeys,
        sortStatus.sortBy,
        sortStatus.orderStatus,
      ],
      queryFn: async ({ pageParam }) => {
        return await props.options.fetcher(sortStatus, pageParam);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage?.paginate_token,
    });

  const flattenData = useMemo(() => {
    return data?.pages
      .flat()
      .map((i) => i.list)
      .flat();
  }, [data?.pages]);

  const onFeedbackItemClick = (alias: string | null) => {
    if (!alias) {
      setSelectedFeedback(null);
      return;
    }
    setSelectedFeedback(alias);
    setNewModal({
      alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK,
      idx: 1,
    });
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: 0,
    });
  }, [props.alias]);

  useTrackModal({
    domain: props.options.domain,
    alias: props.alias,
  });

  const onCloseClick = () => {
    if (chatAlias && chat?.alias === chatAlias) {
      setPopup({
        type: 'confirm',
        popupTheme: 'negative',
        title: 'Would you like to close this window?',
        message: 'If you close the window, the content will be deleted.',
        confirmText: 'Okay',
        onConfirm: () => {
          leaveChat(chatAlias);
          props.closeModal();
        },
      });
    } else {
      props.closeModal();
    }
  };

  useKeymap('Escape', onCloseClick);

  return (
    <ModalStyle status={true} pos={props.pos}>
      <Wrapper f={1}>
        <Flex j="flex-end" a="center" style={{ padding: '16px 32px' }}>
          <Flex g="6px">
            <IconButton icon={<CloseOutlined />} onClick={onCloseClick} />
          </Flex>
        </Flex>
        <Divider />
        <Space h={24} />
        <VFlex padding="0px 32px" g={12}>
          <Flex g={4}>
            <Typo type="label1" className="text-gray-600">
              {props.options.title}
            </Typo>
          </Flex>
          {props.options.subTitle}
        </VFlex>
        <Space h={24} />
        <div className="px-8 flex justify-between items-center">
          <Typo type="label2">
            Total feedback : {commaNumber(data?.pages[0]?.count || 0)}
          </Typo>
          <Sorter
            options={FEEDBACK_SORT_OPTIONS}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
        </div>
        <Space h={24} />
        <List
          isLoading={isLoading}
          data={flattenData}
          className={cn(
            'gap-2 px-8 h-full',
            props.options.socketInfo ? 'pb-50' : 'pb-6',
          )}
          render={(f) => (
            <FeedbackShortItem
              feedback={f}
              onClick={onFeedbackItemClick}
              selected={selectedFeedback === f.alias}
            />
          )}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
        {props.options.socketInfo && (
          <AskSynclyPanel
            chat={chat}
            socketInfo={props.options.socketInfo}
            onSocketConnectChange={setChatAlias}
          />
        )}
      </Wrapper>
    </ModalStyle>
  );
};

const Wrapper = styled(VFlex)`
  overflow: auto;
  height: 100%;
`;

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;
  ${detailModalStyle};
`;

export default FeedbackListModal;
